import React, { Fragment } from 'react';
import { Col, Container, Row } from 'reactstrap';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { actionCreators } from '../../store/Configurator';
import { RouteComponentProps } from 'react-router-dom';
import locale from '../../Locale';
import Helper from '../../Helper';
import Loading from 'react-fullscreen-loading';

interface IProps extends RouteComponentProps<any> {}

interface IState {
	loading: boolean;
	error: string;
}

class ExternalLogin extends React.Component<IProps, IState> {
	private salesDocumentId: string | null = null;
	private salesDocumentConfigurationId: string | null = null;

	constructor(props: any) {
		super(props);
		this.state = {
			loading: true,
			error: '',
		};
	}

	componentDidMount() {
		const params = new URLSearchParams(window.location.search);
		const code = params.get('code');
		this.salesDocumentId = params.get('salesDocumentId');
		this.salesDocumentConfigurationId = params.get('salesDocumentConfigurationId');
		if (!code) {
			this.setState({
				loading: false,
				error: 'Missing login code. Use ?code=xxx',
			});
		} else if (!this.salesDocumentId) {
			this.setState({
				loading: false,
				error: 'Missing sales document id. Use ?salesDocumentId=xxx',
			});
		} else {
			this.exchangeCodeForToken(code);
		}
	}

	async exchangeCodeForToken(code: string) {
		try {
			const response = await Helper.postData<any>('/sso/exchange-code', JSON.stringify({ code }));
			if (response && response.AccessToken) {
				this.setState({ loading: true });
				this.redirectForm(response.AccessToken, response.RefreshToken || '');
			} else {
				this.setState({ loading: false, error: 'Failed to exchange code for token' });
			}
		} catch (error: any) {
			this.setState({ loading: false, error: 'Code exchange failed: ' + error });
		}
	}

	private setToken(
		email: string,
		name: string,
		userType: string,
		token: string,
		refreshToken: string,
		isUserOem: string,
		isUserOemAdmin: string,
		isParentPartner: string,
	) {
		localStorage.removeItem('theme');
		localStorage.removeItem('brandPartnerId');
		if (token !== '') {
			localStorage.setItem('userToken', token);
			localStorage.setItem('userRole', userType);
			localStorage.setItem('refreshToken', refreshToken);
			localStorage.setItem('isUserOEM', isUserOem);
			localStorage.setItem('isUserOEMAdmin', isUserOemAdmin);
			localStorage.setItem('isParentPartner', isParentPartner);
		} else {
			localStorage.removeItem('userToken');
			localStorage.removeItem('userRole');
			localStorage.removeItem('refreshToken');
			localStorage.removeItem('isUserOEM');
			localStorage.removeItem('isUserOEMAdmin');
			localStorage.removeItem('isParentPartner');
		}
		(this.props as any).setAnonymousToken(null);
		(this.props as any).setUser(email, name, userType, []);
		(this.props as any).clearPart();
	}

	setupBrandFromAccountInfo(data: any) {
		if (!data.BrandInfo) {
			return;
		}
		const brandInfo = data.BrandInfo;
		const footer = data.Footer;
		const logo = (brandInfo.Style && brandInfo.Style.HeaderLogo) || '';
		const logotype = (brandInfo.Style && brandInfo.Style.HeaderLogotype) || '';
		const title = (brandInfo && Helper.getLocalizedName(brandInfo.BrandName)) || '';
		const header = (brandInfo.Style && Helper.getLocalizedName(brandInfo.Style.HeaderText)) || '';

		const color = (brandInfo.Style && brandInfo.Style.BackgroundColor) || '';
		const headerFontColor = (brandInfo.Style && brandInfo.Style.HeaderFontColor) || '';
		const buttonColor = (brandInfo.Style && brandInfo.Style.ButtonColor) || '';
		const buttonFontColor = (brandInfo.Style && brandInfo.Style.ButtonFontColor) || '';

		const showExposedBanner = (brandInfo.Style && brandInfo.Style.ShowExposedBanner) || false;
		const exposedBannerText = (brandInfo.Style && brandInfo.Style.ExposedBannerText) || '';
		const exposedBannerUrl = (brandInfo.Style && brandInfo.Style.ExposedBannerUrl) || '';
		const exposedBannerColor = (brandInfo.Style && brandInfo.Style.ExposedBannerColor) || '';
		const exposedBannerFontColor = (brandInfo.Style && brandInfo.Style.ExposedBannerFontColor) || '';

		const configuratorFavicon = (brandInfo.Style && brandInfo.Style.ConfiguratorFavicon) || '';
		const configuratorTitle = (brandInfo.Style && brandInfo.Style.ConfiguratorTitle) || '';

		const theme = {
			logo: logo,
			title: title,
			header: header,
			color: color,
			headerFontColor: headerFontColor,
			buttonColor: buttonColor,
			buttonFontColor: buttonFontColor,
			showExposedBanner: showExposedBanner,
			exposedBannerText: exposedBannerText,
			exposedBannerUrl: exposedBannerUrl,
			exposedBannerColor: exposedBannerColor,
			exposedBannerFontColor: exposedBannerFontColor,
			configuratorFavicon,
			configuratorTitle,
		};

		localStorage.setItem('theme', JSON.stringify(theme));
		localStorage.setItem('brandPartnerId', brandInfo.BrandPartnerId);

		(this.props as any).setBrand(
			logo,
			logotype,
			title,
			header,
			color,
			headerFontColor,
			brandInfo.BrandPartnerId,
			buttonColor,
			buttonFontColor,
			showExposedBanner,
			exposedBannerText,
			exposedBannerUrl,
			exposedBannerColor,
			exposedBannerFontColor,
			configuratorFavicon,
			configuratorTitle,
		);

		const footer1text = (footer && footer.Footer1text) || '';
		const footer1url = (footer && footer.Footer1url) || '';
		const footer2text = (footer && footer.Footer2text) || '';
		const footer2url = (footer && footer.Footer2url) || '';
		const footer3text = (footer && footer.Footer3text) || '';
		const footer3url = (footer && footer.Footer3url) || '';
		(this.props as any).setFooter(footer1text, footer1url, footer2text, footer2url, footer3text, footer3url);
	}

	setDocumentFromData(salesDocumentId: string, data: any) {
		(this.props as any).setDocument(
			salesDocumentId,
			data.InitialSalesDocumentId || '',
			data.DocumentNumber,
			data.ProjectNumber,
			data.Configurations,
			data.IsReadOnly,
			data.DocumentStatusId,
			data.Currency,
		);
	}

	openDocumentConfigurator(salesDocumentId: string) {
		const url = '/document/' + salesDocumentId + '?brandPartnerId=' + Helper.brandPartnerId();
		Helper.getData<any>(url)
			.then((data: any) => {
				if (data.Message) {
					(this.props as any).setError(locale.exception, data.Message);
					this.setState({ loading: false, error: data.Message });
				} else if (data) {
					(this.props as any).clearPart();
					this.setDocumentFromData(salesDocumentId, data);
					this.props.history.push(process.env.REACT_APP_ROOT_PATH + '/external-configurator/selection');
				}
			})
			.catch((error: any) => {
				this.setState({ loading: false, error: 'Failed to load document: ' + error });
			});
	}

	openExistingDocumentConfiguration(salesDocumentId: string, salesDocumentConfigurationId: string) {
		const url = '/document/' + salesDocumentId + '?brandPartnerId=' + Helper.brandPartnerId();
		Helper.getData<any>(url)
			.then((data: any) => {
				if (data.Message) {
					(this.props as any).setError(locale.exception, data.Message);
					this.setState({ loading: false, error: data.Message });
					return;
				}

				const configurations = data.Configurations || [];
				const configuration = configurations.find(
					(item: any) => item.SalesDocumentConfigurationId === salesDocumentConfigurationId,
				);

				if (!configuration) {
					this.setState({ loading: false, error: 'Configuration not found on document' });
					return;
				}

				(this.props as any).clearPart();
				this.setDocumentFromData(salesDocumentId, data);
				(this.props as any).saveConfiguration(
					configuration.ConfigurationId,
					configuration.SalesDocumentConfigurationId,
					configuration.ConfigurationPosition,
				);
				(this.props as any).buildCategories(configuration.ConfigurationId, null, salesDocumentConfigurationId);

				setTimeout(() => {
					this.props.history.push(
						process.env.REACT_APP_ROOT_PATH + '/external-configurator/configuration?bpi=' + Helper.brandPartnerId(),
					);
				}, 500);
			})
			.catch((error: any) => {
				this.setState({ loading: false, error: 'Failed to load document: ' + error });
			});
	}

	async redirectForm(accessToken: string, refreshToken: string) {
		if (!accessToken) {
			this.setState({ loading: false, error: 'Invalid token provided' });
			return;
		}

		const salesDocumentId = this.salesDocumentId;
		if (!salesDocumentId) {
			this.setState({ loading: false, error: 'Missing sales document id' });
			return;
		}

		this.setToken('', '', '', accessToken, refreshToken, '', '', '');
		const url = '/account/info';
		Helper.getData<any>(url)
			.then((data: any) => {
				if (data.Message && data.Message.length > 0) {
					(this.props as any).setError(locale.saving, data.Message);
					this.setState({ loading: false, error: data.Message });
				} else {
					let email = '';
					if (data.Email) {
						email = data.Email;
					}
					let name = '';
					if (data.FirstName) {
						name = data.FirstName;
					}
					if (data.LastName) {
						name += ' ' + data.LastName;
					}
					let userType = '';
					if (data.PartnerUserType) {
						userType = data.PartnerUserType;
					}

					this.setToken(email, name, userType, accessToken, refreshToken, data.IsUserOem, data.IsUserOemAdmin, data.IsParentPartner);
					this.setupBrandFromAccountInfo(data);

					if (this.salesDocumentConfigurationId) {
						this.openExistingDocumentConfiguration(salesDocumentId, this.salesDocumentConfigurationId);
					} else {
						this.openDocumentConfigurator(salesDocumentId);
					}
				}
			})
			.catch((error: any) => {
				this.setState({ loading: false, error: 'Failed to fetch account info: ' + error });
			});
	}

	render() {
		const { loading, error } = this.state;

		return (
			<Fragment>
				{loading ? (
					<Loading loading loaderColor={Helper.themeButtonColor()} />
				) : (
					<Container>
						<Row form>
							<Col md={6} className='login'>
								<h2 style={{ color: 'red' }}>Login Error</h2>
								<p>{error}</p>
							</Col>
						</Row>
					</Container>
				)}
			</Fragment>
		);
	}
}

export default connect(
	(state: any) => state.configurator,
	(dispatch) => bindActionCreators(actionCreators, dispatch),
)(ExternalLogin);
